import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  Users, PlusCircle, Loader2, Pencil, Trash2, Shield, User, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const SubUserManager = () => {
  const navigate = useNavigate();
  const { user, isB2BAdmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'B2B_AGENT_USER'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isB2BAdmin) {
      toast.error('Access denied. Only B2B admins can manage team members.');
      navigate('/dashboard');
    }
  }, [isB2BAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isB2BAdmin) {
      fetchUsers();
    }
  }, [isB2BAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/sub-users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      role: 'B2B_AGENT_USER'
    });
    setShowDialog(true);
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setFormData({
      email: u.email,
      password: '',
      full_name: u.full_name,
      phone: u.phone || '',
      role: u.role,
      is_active: u.is_active
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        await api.put(`/users/sub-users/${editingUser.user_id}`, updateData);
        toast.success('Team member updated');
      } else {
        if (!formData.password) {
          toast.error('Password is required for new team members');
          setSaving(false);
          return;
        }
        await api.post('/users/sub-users', formData);
        toast.success('Team member created');
      }
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Are you sure you want to delete ${u.full_name}?`)) return;

    try {
      await api.delete(`/users/sub-users/${u.user_id}`);
      toast.success('Team member deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const handleToggleStatus = async (u) => {
    try {
      await api.put(`/users/sub-users/${u.user_id}`, { is_active: !u.is_active });
      fetchUsers();
      toast.success(`Team member ${u.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!isB2BAdmin) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
            <p className="text-sm text-gray-500">
              Manage your team's access to create and view quotations
            </p>
          </div>
          <Button onClick={openCreateDialog} data-testid="add-user-btn">
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Team Members</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No team members yet</p>
                <Button onClick={openCreateDialog}>
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Your First Team Member
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div 
                    key={u.user_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    data-testid={`user-row-${u.email}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        u.role === 'B2B_AGENT_ADMIN' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {u.role === 'B2B_AGENT_ADMIN' ? (
                          <Shield className="w-5 h-5 text-purple-600" />
                        ) : (
                          <User className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">{u.full_name}</p>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            u.role === 'B2B_AGENT_ADMIN' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {u.role === 'B2B_AGENT_ADMIN' ? 'Admin' : 'User'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </span>
                          {u.phone && (
                            <span className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {u.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${
                          u.is_active ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Switch
                          checked={u.is_active}
                          onCheckedChange={() => handleToggleStatus(u)}
                          data-testid={`toggle-status-${u.email}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(u)}
                        data-testid={`edit-user-${u.email}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(u)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-user-${u.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Roles Info */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-medium text-gray-900 mb-2">User Roles</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-purple-600 mt-0.5" />
                <div>
                  <span className="font-medium text-gray-900">Admin</span>
                  <p>Can view all team quotations and manage team members</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-blue-600 mt-0.5" />
                <div>
                  <span className="font-medium text-gray-900">User</span>
                  <p>Can create and view only their own quotations</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="John Doe"
                required
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="user@company.com"
                required
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                Password {editingUser && '(leave blank to keep current)'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder={editingUser ? '••••••••' : 'Enter password'}
                required={!editingUser}
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone / WhatsApp (optional)</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="9876543210"
                data-testid="user-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="B2B_AGENT_USER">User</SelectItem>
                  <SelectItem value="B2B_AGENT_ADMIN">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingUser ? 'Update' : 'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SubUserManager;
